import React from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  TooltipProps
} from 'recharts';

interface CurveDataPoint {
  budget: number;
  reach: number;
  impressions: number;
}

interface ScenarioCurve {
  id: string;
  name: string;
  isWinner: boolean;
  data: CurveDataPoint[];
}

interface ReachCurveChartProps {
  // New multi-scenario format
  scenarios?: ScenarioCurve[];
  // Legacy single-curve format (backward compat)
  data?: CurveDataPoint[];
  winnerScenarioId?: string;
  // Shared
  userBudget?: number;
  currencyCode?: string;
}

/**
 * A palette wide enough that two lines on the same chart differ.
 *
 * There were five colours and fifteen scenarios, so every sixth line repeated
 * a colour — with duplicate labels beside them there was no way to tell which
 * curve was which.
 */
const SCENARIO_COLORS = [
  '#8b5cf6', '#f59e0b', '#10b981', '#3b82f6', '#ef4444',
  '#ec4899', '#14b8a6', '#a16207', '#6366f1', '#84cc16',
  '#f97316', '#06b6d4', '#9333ea', '#dc2626', '#0891b2',
];

/**
 * The x-axis window, and its ticks.
 *
 * `Math.max(userBudget * 4, maxDataBudget)` ran the axis to $4,100K on a
 * 50,000 budget, because the reach API returns curve points far past anything
 * the plan would spend. Every difference between scenarios was squeezed into
 * the leftmost 1% of the chart.
 *
 * The window is now four times the budget — enough headroom to see where the
 * curve flattens — clamped so it never exceeds the data (no empty space to the
 * right of every curve) and never falls below the budget itself (the budget
 * reference line must stay on the chart).
 */
export const computeBudgetAxis = (
  userBudget: number | undefined,
  maxDataBudget: number
): { axisMax: number; ticks: number[] } => {
  const budget = userBudget && userBudget > 0 ? userBudget : 0;
  const data = maxDataBudget > 0 ? maxDataBudget : 0;

  let axisMax: number;
  if (budget > 0) {
    const window = budget * 4;
    axisMax = data > 0 ? Math.min(window, data) : window;
    // Keep the budget line visible with a little air after it.
    axisMax = Math.max(axisMax, budget * 1.1);
  } else {
    axisMax = data;
  }
  if (axisMax <= 0) axisMax = 100000;

  // Round up to a whole number of ticks, aiming for 5-10 of them.
  const rawStep = axisMax / 8;
  const magnitude = Math.pow(10, Math.floor(Math.log10(rawStep)));
  const step = [1, 2, 2.5, 5, 10]
    .map(m => m * magnitude)
    .find(candidate => candidate >= rawStep) ?? magnitude * 10;

  axisMax = Math.ceil(axisMax / step) * step;
  const ticks: number[] = [];
  for (let t = 0; t <= axisMax + step / 2; t += step) ticks.push(Math.round(t));
  return { axisMax, ticks };
};

/**
 * One distinct label per scenario.
 *
 * Two scenarios can carry the same display name — the screenshot showed
 * "Perfumes_Fragrance_Skippable" twice — so the legend named two different
 * curves identically.
 */
export const disambiguateLabels = (
  scenarios: Array<{ id: string; name: string }>
): string[] => {
  const counts = new Map<string, number>();
  scenarios.forEach(s => {
    const key = s.name || s.id;
    counts.set(key, (counts.get(key) ?? 0) + 1);
  });
  const seen = new Map<string, number>();
  return scenarios.map(s => {
    const base = s.name || s.id || 'Scenario';
    if ((counts.get(s.name || s.id) ?? 0) < 2) return base;
    const n = (seen.get(base) ?? 0) + 1;
    seen.set(base, n);
    return `${base} (${n})`;
  });
};

const CustomTooltip: React.FC<TooltipProps<number, string> & { currencyCode?: string }> = ({ active, payload, label, currencyCode }) => {
  if (active && payload && payload.length) {
    const budget = typeof label === 'number' ? label : Number(label);
    return (
      <div style={{
        backgroundColor: 'rgba(255, 255, 255, 0.97)',
        border: '1px solid #e5e7eb',
        borderRadius: '8px',
        padding: '12px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        maxWidth: '260px'
      }}>
        <p style={{ margin: '0 0 8px 0', fontSize: '12px', color: '#6b7280' }}>
          Budget: {budget.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} {currencyCode}
        </p>
        {payload.map((entry, i) => (
          <div key={i} style={{ marginBottom: i < payload.length - 1 ? '6px' : 0 }}>
            <p style={{ margin: 0, fontWeight: 600, color: entry.color, fontSize: '13px' }}>
              {entry.name}
            </p>
            <p style={{ margin: '1px 0 0 0', fontSize: '12px', color: '#374151' }}>
              Reach: {Number(entry.value).toLocaleString('en-US')}
            </p>
          </div>
        ))}
      </div>
    );
  }
  return null;
};

const ReachCurveChart: React.FC<ReachCurveChartProps> = ({
  scenarios,
  data,
  winnerScenarioId,
  userBudget,
  currencyCode = 'USD',
}) => {
  // Normalise to multi-scenario format
  const normalised: ScenarioCurve[] = scenarios
    ? scenarios
    : data && data.length > 0
      ? [{ id: winnerScenarioId || 'Scenario', name: winnerScenarioId || 'Reach', isWinner: true, data }]
      : [];

  if (normalised.length === 0) return null;

  const hasWinner = normalised.some(s => s.isWinner);

  // Compute X-axis range across all scenarios
  const allBudgetValues = normalised.flatMap(s => s.data.map(p => p.budget));
  const maxDataBudget = allBudgetValues.length > 0 ? Math.max(...allBudgetValues) : 0;

  const { axisMax, ticks } = computeBudgetAxis(userBudget, maxDataBudget);
  const labels = disambiguateLabels(normalised);

  // Merge all scenario data into one unified dataset keyed by budget.
  // Each scenario's reach is stored under its own id key so recharts can
  // resolve per-line values independently and the tooltip shows correct values.
  type MergedPoint = { budget: number; [key: string]: number };
  const budgetSet = new Set<number>();
  normalised.forEach(s =>
    s.data.filter(p => p.budget >= 0 && p.budget <= axisMax).forEach(p => budgetSet.add(p.budget))
  );
  const sortedBudgets = Array.from(budgetSet).sort((a, b) => a - b);
  const mergedData: MergedPoint[] = sortedBudgets.map(budget => {
    const point: MergedPoint = { budget };
    normalised.forEach(s => {
      const match = s.data.find(p => p.budget === budget);
      if (match !== undefined) point[s.id] = match.reach;
    });
    return point;
  });

  return (
    <div style={{ width: '100%', marginTop: '24px', marginBottom: '24px' }}>
      <h3 style={{ marginBottom: '4px', fontSize: '18px', fontWeight: 600 }}>Reach Curves — All Scenarios</h3>
      <p style={{ marginBottom: '16px', fontSize: '13px', color: '#6b7280' }}>
        Budget vs on-target reach per scenario (Google Ads ReachPlanService)
        {hasWinner && ' · Winner shown at full opacity'}
      </p>

      <ResponsiveContainer width="100%" height={420}>
        <LineChart data={mergedData} margin={{ top: 10, right: 30, left: 20, bottom: 30 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
          <XAxis
            dataKey="budget"
            type="number"
            domain={[0, axisMax]}
            ticks={ticks}
            // No currency symbol: the axis label names the session's currency.
            tickFormatter={(v) => `${(v / 1000).toFixed(0)}K`}
            label={{ value: `Budget (${currencyCode})`, position: 'insideBottom', offset: -10 }}
            stroke="#6b7280"
          />
          <YAxis
            tickFormatter={(v) => (v / 1000).toFixed(0) + 'k'}
            label={{ value: 'On-target Reach', angle: -90, position: 'insideLeft', offset: 10 }}
            stroke="#6b7280"
          />
          <Tooltip content={<CustomTooltip currencyCode={currencyCode} />} />
          {normalised.map((scenario, i) => {
            const color = SCENARIO_COLORS[i % SCENARIO_COLORS.length];
            const isWinner = scenario.isWinner;
            const label = isWinner && hasWinner
              ? `${labels[i]} ★ Winner`
              : labels[i];
            return (
              <Line
                key={scenario.id}
                type="monotone"
                dataKey={scenario.id}
                name={label}
                stroke={color}
                strokeWidth={isWinner ? 3 : 1.5}
                strokeOpacity={!hasWinner || isWinner ? 1 : 0.3}
                dot={isWinner ? { fill: color, r: 3 } : false}
                activeDot={isWinner ? { r: 6 } : { r: 3 }}
                connectNulls
              />
            );
          })}
          {userBudget && userBudget > 0 && (
            <ReferenceLine
              x={userBudget}
              stroke="#e11d48"
              strokeWidth={2}
              strokeDasharray="5 5"
              label={{ value: 'Your Budget', position: 'top', fill: '#e11d48', fontWeight: 600, fontSize: 12 }}
            />
          )}
        </LineChart>
      </ResponsiveContainer>

      {/* The legend lives outside the chart on purpose. Recharts takes the
          space a <Legend> needs out of the plot, so fifteen stacked entries
          left the curves a few pixels tall. Out here it wraps, and scrolls
          past a few rows, and the plot always keeps its full height. */}
      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: '6px 16px',
        marginTop: '12px',
        maxHeight: '104px',
        overflowY: 'auto',
        paddingRight: '4px',
      }}>
        {normalised.map((scenario, i) => (
          <span
            key={scenario.id}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: '6px',
              fontSize: '12px',
              color: scenario.isWinner || !hasWinner ? '#374151' : '#9ca3af',
              fontWeight: scenario.isWinner && hasWinner ? 700 : 500,
            }}
          >
            <span style={{
              width: '14px', height: '3px', borderRadius: '2px', flexShrink: 0,
              background: SCENARIO_COLORS[i % SCENARIO_COLORS.length],
              opacity: !hasWinner || scenario.isWinner ? 1 : 0.4,
            }} />
            {labels[i]}
            {scenario.isWinner && hasWinner && ' ★'}
          </span>
        ))}
      </div>

      {userBudget && userBudget > 0 && (
        <p style={{ marginTop: '8px', fontSize: '13px', color: '#6b7280' }}>
          Your budget: {userBudget.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} {currencyCode}
        </p>
      )}
    </div>
  );
};

export default ReachCurveChart;